import {
	DeleteOutlined,
	DownloadOutlined,
	LeftOutlined,
	PlusOutlined,
	SearchOutlined,
} from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Input, message, Popconfirm, Progress, Select, Table, Tag } from "antd";
import { useState } from "react";
import { useLocation, useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import type { AppRole } from "@/shared/config/navigation";
import {
	canCreateSale as canCreateSaleByRole,
	canDeleteSale as canDeleteSaleByRole,
} from "@/shared/config/permissions";
import { parseApiError } from "@/shared/utils/error";
import { formatMoney } from "@/shared/utils/money";
import { getSales, submitSaleDelete } from "../services/sales.backend";
import type { PaymentType, Sale, SaleStatus } from "../types/sales.types";
import { exportSalesToExcel } from "../utils/sales-export";

type SalesLocationState = {
	fromDashboard?: boolean;
	manager?: string | null;
};

export function SalesPage() {
	const navigate = useNavigate();
	const location = useLocation();
	const { role } = useOutletContext<{ role: AppRole }>();
	const canCreateSale = canCreateSaleByRole(role);
	const canDeleteSale = canDeleteSaleByRole(role);
	const queryClient = useQueryClient();
	const locationState = location.state as SalesLocationState | null;

	const fromDashboard = locationState?.fromDashboard === true;

	// Filters states
	const [searchQuery, setSearchQuery] = useState("");
	const [selectedBuilding, setSelectedBuilding] = useState<string | null>(null);
	const [selectedManager, setSelectedManager] = useState(locationState?.manager || null);
	const [selectedStatus, setSelectedStatus] = useState<string | null>(null);

	const buildingOptions = ["Dream", "Yunusobod", "Unicorp City"];
	const managerOptions = ["Sardor T.", "Kamol X.", "Aziza R."];
	const statusOptions = ["Faol", "Bekor qilingan", "Yakunlangan"];

	const [page, setPage] = useState(1);
	const pageSize = 10;
	const [searchParams] = useSearchParams();
	const orgIdFromUrl = searchParams.get("orgId") || undefined;
	const [isExporting, setIsExporting] = useState(false);

	const statusFilterValue =
		selectedStatus === "Faol"
			? "faol"
			: selectedStatus === "Bekor qilingan"
				? "bekor"
				: selectedStatus === "Yakunlangan"
					? "yakunlangan"
					: undefined;

	// Use Query for sales
	const { data, isLoading } = useQuery({
		queryKey: [
			"sales",
			searchQuery,
			selectedBuilding,
			selectedManager,
			selectedStatus,
			page,
			orgIdFromUrl,
		],
		queryFn: () =>
			getSales({
				search: searchQuery || undefined,
				status: statusFilterValue,
				page,
				limit: pageSize,
				orgId: orgIdFromUrl,
			}),
	});

	const handleExport = async () => {
		setIsExporting(true);
		try {
			const count = await exportSalesToExcel({
				search: searchQuery || undefined,
				status: statusFilterValue,
				orgId: orgIdFromUrl,
			});
			message.success(`${count} ta sotuv eksport qilindi`);
		} catch {
			message.error("Eksport qilishda xatolik yuz berdi");
		} finally {
			setIsExporting(false);
		}
	};

	const filteredSales = data?.items || [];
	const totalItems = data?.meta?.total || 0;

	const deleteSaleMutation = useMutation({
		mutationFn: submitSaleDelete,
		onSuccess: async () => {
			message.success("Sotuv muvaffaqiyatli o'chirildi!");
			await queryClient.invalidateQueries({ queryKey: ["sales"] });
		},
		onError: (error) => {
			message.error(parseApiError(error) || "O'chirishda xatolik yuz berdi");
		},
	});

	const columns = [
		{
			title: "Mijoz",
			key: "client",
			render: (_: unknown, record: Sale) => {
				const initials = record.clientName
					.split(" ")
					.map((n) => n[0])
					.join("")
					.toUpperCase()
					.substring(0, 2);
				return (
					<div className="crm-org-name-cell">
						<span className="crm-mini-avatar blue">{initials}</span>
						<div>
							<strong>{record.clientName}</strong>
							<span>{record.phone}</span>
						</div>
					</div>
				);
			},
		},
		{
			title: "Xona",
			key: "room",
			render: (_: unknown, record: Sale) => (
				<div>
					<span className="font-semibold">{record.roomNumber}-xona</span>
					<div className="text-xs text-gray-400">
						{record.buildingName} • {record.blockName}
					</div>
				</div>
			),
		},
		{
			title: "Shartnoma narxi",
			key: "price",
			render: (_: unknown, record: Sale) => (
				<div>
					<div className="font-bold text-blue-600">{formatMoney(record.finalPrice)} UZS</div>
				</div>
			),
		},
		{
			title: "To'lov turi",
			dataIndex: "paymentType",
			key: "paymentType",
			render: (value: PaymentType) => (
				<Tag color={value === "muddatli" ? "purple" : "cyan"}>
					{value === "muddatli" ? "Bo'lib to'lash" : "100% Naqd"}
				</Tag>
			),
		},
		{
			title: "To'langan",
			key: "paid",
			render: (_: unknown, record: Sale) => {
				let status: "success" | "normal" | "exception" | "active" = "normal";
				if (record.status === "yakunlangan") {
					status = "success";
				}
				if (record.status === "bekor") {
					status = "exception";
				}

				return (
					<div style={{ width: 140 }}>
						<div className="flex justify-between items-center text-xs mb-1">
							<span className="font-semibold">{record.paidPercent}%</span>
							<span className="text-gray-400">{formatMoney(record.paidAmount)}</span>
						</div>
						<Progress percent={record.paidPercent} showInfo={false} status={status} size="small" />
					</div>
				);
			},
		},
		{
			title: "Menejer",
			dataIndex: "managerName",
			key: "managerName",
		},
		{
			title: "Holat",
			dataIndex: "status",
			key: "status",
			render: (value: SaleStatus) => {
				let color = "blue";
				let text = "Faol";
				if (value === "bekor") {
					color = "red";
					text = "Bekor qilingan";
				} else if (value === "yakunlangan") {
					color = "green";
					text = "Yakunlangan";
				}
				return <Tag color={color}>{text}</Tag>;
			},
		},
		...(canDeleteSale
			? [
					{
						title: "",
						key: "actions",
						render: (_: unknown, record: Sale) =>
							record.status === "bekor" ? (
								<Popconfirm
									title="Sotuvni o'chirish"
									description="Rostdan ham ushbu sotuvni o'chirmoqchimisiz?"
									onConfirm={(e) => {
										e?.stopPropagation();
										deleteSaleMutation.mutate(record.id);
									}}
									onCancel={(e) => e?.stopPropagation()}
									okText="Ha"
									cancelText="Yo'q"
									okButtonProps={{ danger: true }}
								>
									<Button
										icon={<DeleteOutlined />}
										size="small"
										danger
										onClick={(e) => e.stopPropagation()}
										aria-label="Sotuvni o'chirish"
									/>
								</Popconfirm>
							) : null,
					},
				]
			: []),
	];

	return (
		<div className="crm-sales">
			<div className="crm-page-head">
				<div>
					<div style={{ display: "flex", alignItems: "center", gap: 12 }}>
						{fromDashboard && (
							<Button
								type="text"
								icon={<LeftOutlined />}
								onClick={() => navigate(-1)}
								style={{ color: "var(--crm-text-secondary)" }}
							/>
						)}
						<h1>Sotuvlar (Shartnomalar)</h1>
					</div>
					<p>
						{fromDashboard ? (
							<span style={{ color: "var(--crm-text-secondary)" }}>Bosh sahifa / </span>
						) : null}
						Kompaniya bo'yicha sotuv shartnomalari ro'yxati · {filteredSales.length} ta
					</p>
				</div>
				<div style={{ display: "flex", gap: 12 }}>
					<Button icon={<DownloadOutlined />} onClick={handleExport} loading={isExporting}>
						Eksport
					</Button>
					{canCreateSale ? (
						<Button
							className="crm-primary-btn"
							type="primary"
							icon={<PlusOutlined />}
							onClick={() => navigate("/sales/new")}
						>
							Yangi sotuv yaratish
						</Button>
					) : (
						<span className="crm-badge violet">Super Admin sotuv yarata olmaydi</span>
					)}
				</div>
			</div>

			<div className="crm-card mb-4">
				<div className="crm-card-body p-4 flex flex-wrap gap-3 items-center">
					<Input
						placeholder="Mijoz, xona yoki shartnoma raqami..."
						prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
						style={{ width: 240 }}
					/>

					<Select
						placeholder="Bino bo'yicha"
						allowClear
						value={selectedBuilding}
						onChange={setSelectedBuilding}
						options={buildingOptions.map((item) => ({ label: item, value: item }))}
						style={{ width: 150 }}
					/>

					<Select
						placeholder="Menejer bo'yicha"
						allowClear
						value={selectedManager}
						onChange={setSelectedManager}
						options={managerOptions.map((item) => ({ label: item, value: item }))}
						style={{ width: 150 }}
					/>

					<Select
						placeholder="Holat bo'yicha"
						allowClear
						value={selectedStatus}
						onChange={setSelectedStatus}
						options={statusOptions.map((item) => ({ label: item, value: item }))}
						style={{ width: 150 }}
					/>
				</div>
			</div>

			<div className="crm-card crm-table-card">
				<Table
					dataSource={filteredSales}
					columns={columns}
					rowKey="id"
					loading={isLoading}
					scroll={{ x: 960 }}
					onRow={(record) => ({
						onClick: () => navigate(`/sales/${record.id}`),
						style: { cursor: "pointer" },
					})}
					pagination={{
						current: page,
						pageSize: pageSize,
						total: totalItems,
						onChange: (p) => setPage(p),
					}}
				/>
			</div>
		</div>
	);
}

export default SalesPage;
